import React from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import DragHandle from "../../elements/DragHandle.jsx";
import InstitutionBadge from "../../components/InstitutionBadge.jsx";
import MaterialIcon from "../../elements/MaterialIcon.jsx";
import AccountRow from "./AccountRow.jsx";
import { accountBalanceOn, accountLimitOn } from "../../../utilities/accountSections.js";
import { visibleAccounts } from "../../../utilities/closedAccounts.js";

/**
 * The page's other view: accounts under the institutions that hold them, to drag into order and to
 * edit the institutions themselves. The droppable ids and types are the ones DashboardPage's drag
 * handler already routes to the reorder actions.
 *
 * Institutions with no accounts come last and do not drag: the reorder counts only those with an
 * account showing, so dragging one would move something else.
 */
export default function InstitutionArrange({ institutions, emptyInstitutions, showClosedAccounts, date, hints = null, onOpenAccount, onOpenInstitution }) {
	return (
		<div className='institution_arrange'>
			<Droppable
				droppableId='plaidInstitutionDroppables'
				type='PLAID_INSTITUTION'>
				{(provided) => (
					<div
						ref={provided.innerRef}
						{...provided.droppableProps}
					>
						{institutions.map((institution, index) => (
							<Draggable
								draggableId={`draggablePlaidInstitutionItem-${institution.id}`}
								index={index}
								key={institution.id}
							>
								{(provided) => (
									<div
										className='institution_arrange_group'
										ref={provided.innerRef}
										{...provided.draggableProps}
									>
										<InstitutionHeading
											institution={institution}
											dragHandleProps={provided.dragHandleProps}
											onOpen={onOpenInstitution}
										/>
										<InstitutionAccounts
											institution={institution}
											showClosedAccounts={showClosedAccounts}
											date={date}
											hints={hints}
											onOpenAccount={onOpenAccount}
										/>
									</div>
								)}
							</Draggable>
						))}
						{provided.placeholder}
					</div>
				)}
			</Droppable>
			{emptyInstitutions.map((institution) => (
				<div key={institution.id} className='institution_arrange_group empty_institution'>
					<InstitutionHeading
						institution={institution}
						onOpen={onOpenInstitution}
					/>
					<div className='institution_arrange_empty'>No accounts yet</div>
				</div>
			))}
		</div>
	);
}

function InstitutionHeading({ institution, dragHandleProps = null, onOpen }) {
	return (
		<div className='institution_arrange_heading'>
			{dragHandleProps && <DragHandle {...dragHandleProps} />}
			<InstitutionBadge
				institution={institution.institution}
				name={institution.getName()}
				color={institution.getColor()}
			/>
			<span className='institution_group_name'>{institution.getName()}</span>
			<button
				type='button'
				className='btn-flat account_edit_button'
				aria-label={`Edit ${institution.getName()}`}
				title='Edit institution'
				onClick={(event) => onOpen(institution, event.currentTarget)}
			>
				<MaterialIcon>edit</MaterialIcon>
			</button>
		</div>
	);
}

/** `hints` is twinHints() of every account in the budget, by id. */
function InstitutionAccounts({ institution, showClosedAccounts, date, hints, onOpenAccount }) {
	const accounts = visibleAccounts(institution, showClosedAccounts);
	return (
		<Droppable
			droppableId={`plaidInnerAccountDroppables-${institution.id}`}
			type='PLAID_INNER_ACCOUNT'>
			{(provided) => (
				<div
					className='institution_arrange_accounts'
					ref={provided.innerRef}
					{...provided.droppableProps}
				>
					{accounts.map((account, index) => (
						<Draggable
							draggableId={`draggablePlaidInnerAccountItem-${account.id}`}
							index={index}
							key={account.id}
						>
							{(provided) => (
								<div
									ref={provided.innerRef}
									{...provided.draggableProps}
								>
									<AccountRow
										account={account}
										institution={institution}
										balance={accountBalanceOn(account, date)}
										limit={accountLimitOn(account, date)}
										showInstitution={false}
										hint={hints ? hints.get(account.id) : null}
										dragHandleProps={provided.dragHandleProps}
										opens='editor'
										onOpen={onOpenAccount}
									/>
								</div>
							)}
						</Draggable>
					))}
					{provided.placeholder}
				</div>
			)}
		</Droppable>
	);
}
